// CSV インポート用ユーティリティ。
// Expo 版 CSVImporter.ts の HEADER_ALIASES を Web の BusinessCard 型に合わせて移植。
//
// パースは Papa Parse、マッピングは自動推測 + ユーザー手動修正可。

import Papa from "papaparse";
import type { BusinessCard, ColumnMap } from "./types";

/** マッピング可能な BusinessCard フィールド(画像系・rowIndex は除外) */
export type MappableField =
  | "no"
  | "acquiredAt"
  | "name"
  | "company"
  | "department"
  | "title"
  | "postalCode"
  | "address"
  | "phone"
  | "mobile"
  | "fax"
  | "email1"
  | "email2"
  | "url"
  | "memo"
  | "nameEn"
  | "companyEn"
  | "departmentEn"
  | "titleEn"
  | "addressEn"
  | "tags";

export const FIELD_LABELS: Record<MappableField, string> = {
  no: "No(通し番号)",
  acquiredAt: "取得日",
  name: "氏名",
  company: "会社",
  department: "部署",
  title: "役職",
  postalCode: "郵便番号",
  address: "住所",
  phone: "電話",
  mobile: "携帯",
  fax: "FAX",
  email1: "メール1",
  email2: "メール2",
  url: "URL",
  memo: "メモ",
  nameEn: "氏名_英",
  companyEn: "会社_英",
  departmentEn: "部署_英",
  titleEn: "役職_英",
  addressEn: "住所_英",
  tags: "タグ",
};

/**
 * 各種 CSV のヘッダ → MappableField のマッピング。
 * 大文字小文字を無視するため、キーは全部小文字 or 全角ひらがな・漢字。
 * (lookupAlias で渡す前に lowercase + trim する)
 */
const HEADER_ALIASES: Record<string, MappableField> = {
  // No
  no: "no",
  番号: "no",
  通し番号: "no",
  number: "no",
  "#": "no",

  // 取得日
  取得日: "acquiredAt",
  登録日: "acquiredAt",
  作成日: "acquiredAt",
  date: "acquiredAt",
  acquiredat: "acquiredAt",
  "acquired at": "acquiredAt",
  created: "acquiredAt",
  "created date": "acquiredAt",
  "登録日時": "acquiredAt",

  // 氏名
  氏名: "name",
  名前: "name",
  姓名: "name",
  fullname: "name",
  "full name": "name",
  name: "name",
  "氏名 (フリガナ)": "name",

  // 会社
  会社: "company",
  会社名: "company",
  company: "company",
  organization: "company",
  org: "company",

  // 部署
  部署: "department",
  department: "department",
  dept: "department",

  // 役職
  役職: "title",
  肩書き: "title",
  肩書: "title",
  title: "title",
  position: "title",

  // 電話
  電話: "phone",
  電話番号: "phone",
  tel: "phone",
  phone: "phone",
  "phone (work)": "phone",

  // 携帯
  携帯: "mobile",
  携帯電話: "mobile",
  mobile: "mobile",
  "phone (mobile)": "mobile",
  cell: "mobile",

  // FAX
  fax: "fax",
  ファックス: "fax",

  // メール
  メール: "email1",
  メール1: "email1",
  メールアドレス: "email1",
  email: "email1",
  "e-mail": "email1",
  email1: "email1",
  mail: "email1",
  "email (work)": "email1",
  メール2: "email2",
  email2: "email2",
  "email (other)": "email2",
  "e-mail 2": "email2",

  // URL
  url: "url",
  website: "url",
  web: "url",
  ホームページ: "url",

  // 郵便番号
  郵便番号: "postalCode",
  zip: "postalCode",
  zipcode: "postalCode",
  postalcode: "postalCode",
  "postal code": "postalCode",

  // 住所
  住所: "address",
  address: "address",

  // メモ
  メモ: "memo",
  備考: "memo",
  note: "memo",
  notes: "memo",
  comment: "memo",

  // 英語面
  氏名_英: "nameEn",
  "氏名(英)": "nameEn",
  name_en: "nameEn",
  fullname_en: "nameEn",
  会社_英: "companyEn",
  "会社(英)": "companyEn",
  company_en: "companyEn",
  部署_英: "departmentEn",
  "部署(英)": "departmentEn",
  department_en: "departmentEn",
  役職_英: "titleEn",
  "役職(英)": "titleEn",
  title_en: "titleEn",
  住所_英: "addressEn",
  "住所(英)": "addressEn",
  address_en: "addressEn",

  // タグ
  タグ: "tags",
  tag: "tags",
  tags: "tags",
  ラベル: "tags",
};

/** 入力ヘッダから MappableField を推測。見つからなければ undefined */
export function lookupAlias(rawHeader: string): MappableField | undefined {
  const k = rawHeader.trim().toLowerCase();
  return HEADER_ALIASES[k] ?? HEADER_ALIASES[rawHeader.trim()];
}

/** CSV のヘッダ配列から自動マッピングを生成 */
export function autoMapColumns(headers: string[]): ColumnMap {
  const map: ColumnMap = {};
  // 同じフィールドに複数列がマッチした場合は最初のものを優先
  const used = new Set<MappableField>();
  for (const h of headers) {
    const field = lookupAlias(h);
    if (field && !used.has(field)) {
      (map as Record<string, string>)[field] = h;
      used.add(field);
    }
  }
  return map;
}

// ============================================================
// パース
// ============================================================

export interface ParsedCSV {
  headers: string[];
  rows: Record<string, string>[];
  errors: string[];
}

/**
 * File をテキスト読み込み → Papa Parse で配列化。
 * UTF-8 / Shift-JIS どちらも極力読めるように TextDecoder で切替。
 */
export async function parseCsvFile(file: File): Promise<ParsedCSV> {
  const buffer = await file.arrayBuffer();
  let text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
  // 文字化けっぽい(置換文字大量)場合は Shift-JIS を試す
  if (text.includes("�")) {
    try {
      text = new TextDecoder("shift_jis", { fatal: false }).decode(buffer);
    } catch {
      // ignore
    }
  }

  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });

  return {
    headers: parsed.meta.fields ?? [],
    rows: parsed.data,
    errors: parsed.errors.map(
      (e) => `row ${e.row ?? "?"}: ${e.code} ${e.message}`,
    ),
  };
}

// ============================================================
// 行 → BusinessCard 変換
// ============================================================

export interface ConvertOptions {
  /** 全行に追加するタグ(CSV由来のタグとマージ) */
  bulkTags?: string[];
  /** 取得日が空の時に「今日」を自動設定する(デフォルト true) */
  fillTodayIfEmpty?: boolean;
}

/**
 * ColumnMap に従って 1 行を BusinessCard に変換。
 * 未マッピングの列は memo に集約。
 * tags は CSV の値を区切り文字で分割。
 */
export function rowToCard(
  row: Record<string, string>,
  columnMap: ColumnMap,
  options: ConvertOptions = {},
): BusinessCard {
  const card: BusinessCard = { name: "" };
  const unmapped: string[] = [];

  // ColumnMap は { フィールド名 : CSV列ヘッダ } の形
  const reverseMap = new Map<string, MappableField>();
  for (const [field, header] of Object.entries(columnMap)) {
    if (header) reverseMap.set(header, field as MappableField);
  }

  for (const [header, value] of Object.entries(row)) {
    const v = (value ?? "").trim();
    if (!v) continue;

    const field = reverseMap.get(header);
    if (!field) {
      unmapped.push(`${header}: ${v}`);
      continue;
    }

    if (field === "tags") {
      const existing = card.tags ?? [];
      const fresh = v
        .split(/[,、，;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      card.tags = [...new Set([...existing, ...fresh])];
    } else {
      (card as unknown as Record<string, string>)[field] = v;
    }
  }

  // 未マッピング列はメモに集約
  if (unmapped.length > 0) {
    const extra = unmapped.join("\n");
    card.memo = card.memo ? `${card.memo}\n${extra}` : extra;
  }

  // 名前が空でも一応許容(後段で警告)
  card.name = card.name ?? "";

  // 取得日: 空なら設定により「今日 (YYYY-MM-DD)」or「空のまま」
  // VCF 側と書式を揃えるためローカル日付の文字列を入れる。
  const fillToday = options.fillTodayIfEmpty ?? true;
  if (!card.acquiredAt && fillToday) {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    card.acquiredAt = `${y}-${m}-${day}`;
  }

  // 一括タグ付与
  if (options.bulkTags && options.bulkTags.length > 0) {
    const existing = card.tags ?? [];
    card.tags = [...new Set([...existing, ...options.bulkTags])];
  }

  return card;
}

export function convertRowsToCards(
  rows: Record<string, string>[],
  columnMap: ColumnMap,
  options: ConvertOptions = {},
): BusinessCard[] {
  return rows.map((r) => rowToCard(r, columnMap, options));
}
